import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLang } from '../i18n/LangContext';

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? 'bg-krug-navy/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-8 h-[72px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white p-1 shadow-[0_0_20px_rgba(245,158,11,0.3)]">
            <img src="/assets/krug-logo.png" alt="KRUG logo" className="w-full h-full object-contain" />
          </span>
          <span className="font-heading font-extrabold tracking-[0.18em] text-lg text-white">KRUG</span>
        </Link>

        <ul className="hidden lg:flex items-center gap-8">
          {t.nav.links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={l.testid}
                className="text-sm font-medium text-slate-300 hover:text-krug-goldbright transition-colors duration-300"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3 md:gap-4">
          <div data-testid="language-switcher" className="hidden md:flex items-center rounded-full border border-white/15 p-1">
            {['BS', 'DE', 'EN'].map((code) => {
              const isActive = lang === code.toLowerCase();
              const enabled = true;
              return (
                <button
                  key={code}
                  type="button"
                  data-testid={`lang-${code.toLowerCase()}`}
                  disabled={!enabled}
                  onClick={() => enabled && setLang(code.toLowerCase())}
                  title={enabled ? code : t.nav.soonTitle}
                  className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors duration-300 ${
                    isActive ? 'bg-krug-gold text-krug-navy' : enabled ? 'text-slate-300 hover:text-white' : 'text-slate-600 cursor-not-allowed'
                  }`}
                >
                  {code}
                </button>
              );
            })}
          </div>

          <a
            href="#waitlist"
            data-testid="nav-cta-join"
            className="hidden sm:inline-flex items-center rounded-full bg-krug-gold px-5 py-2.5 text-sm font-bold text-krug-navy hover:bg-krug-goldbright transition-colors duration-300"
          >
            {t.nav.cta}
          </a>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label={open ? 'Zatvori meni' : 'Otvori meni'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-krug-navy/95 backdrop-blur-xl border-b border-white/10"
          >
            <ul className="px-6 py-6 space-y-4">
              {t.nav.links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`mobile-${l.testid}`}
                    onClick={() => setOpen(false)}
                    className="block font-heading text-xl font-bold text-white hover:text-krug-goldbright transition-colors duration-300"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="pt-3">
                <a
                  href="#waitlist"
                  data-testid="mobile-nav-cta-join"
                  onClick={() => setOpen(false)}
                  className="inline-flex rounded-full bg-krug-gold px-6 py-3 text-sm font-bold text-krug-navy"
                >
                  {t.nav.cta}
                </a>
              </li>
              <li className="pt-2">
                <div data-testid="mobile-language-switcher" className="inline-flex items-center rounded-full border border-white/15 p-1">
                  {['BS', 'DE', 'EN'].map((code) => {
                    const isActive = lang === code.toLowerCase();
                    const enabled = code !== 'EN';
                    return (
                      <button
                        key={code}
                        type="button"
                        data-testid={`mobile-lang-${code.toLowerCase()}`}
                        disabled={!enabled}
                        onClick={() => enabled && setLang(code.toLowerCase())}
                        className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors duration-300 ${
                          isActive ? 'bg-krug-gold text-krug-navy' : enabled ? 'text-slate-300' : 'text-slate-600 cursor-not-allowed'
                        }`}
                      >
                        {code}
                      </button>
                    );
                  })}
                </div>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
